import { useEffect, useRef } from 'react';
import { socket } from '~/constants/sockets/api';
import {
  useBuildContext,
  useChatContext,
  useKeyContext,
  useNotiContext
} from '~/contexts';
import type {
  BuildLiveRunUsageMetric,
  BuildLiveRunState,
  BuildWorkspaceSnapshot
} from '~/contexts/Build/reducer';
import {
  getBuildResumeRunStateReplayKey,
  type BuildRunEventEnvelopePayload,
  type BuildResumeRunStatePayload,
  normalizeBuildRunEventEnvelope,
  normalizeBuildResumeRunState,
  replayBuildResumeRunState
} from '~/contexts/Build/resumeRunState';
import { createFallbackBuildRunMessageId } from '~/contexts/Build/messageIdentity';
import { resolveBuildContributionLumineFixSocketUpdate } from '~/helpers/buildContributionSubmissionHelpers';
import { getViewerCollaborationBuildSummaryPatch } from '~/helpers/buildSummaryHelpers';

function getBuildRequestLimitsFromPayload(payload: any) {
  return payload?.requestLimits || payload?.billing?.snapshot || null;
}

export default function useBuildSocket() {
  const userId = useKeyContext((v) => v.myState.userId);
  const buildRuns = useBuildContext((v) => v.state.buildRuns) as Record<
    string,
    BuildLiveRunState
  >;
  const buildRunRequestMap = useBuildContext(
    (v) => v.state.buildRunRequestMap
  ) as Record<string, number>;
  const buildWorkspaces = useBuildContext(
    (v) => v.state.buildWorkspaces
  ) as Record<string, BuildWorkspaceSnapshot>;
  const onRegisterBuildRun = useBuildContext(
    (v) => v.actions.onRegisterBuildRun
  );
  const onUpdateBuildRunStatus = useBuildContext(
    (v) => v.actions.onUpdateBuildRunStatus
  );
  const onUpdateBuildRunStream = useBuildContext(
    (v) => v.actions.onUpdateBuildRunStream
  );
  const onApplyBuildRunRunningSnapshot = useBuildContext(
    (v) => v.actions.onApplyBuildRunRunningSnapshot
  );
  const onAppendBuildRunEvent = useBuildContext(
    (v) => v.actions.onAppendBuildRunEvent
  );
  const onCompleteBuildRun = useBuildContext(
    (v) => v.actions.onCompleteBuildRun
  );
  const onFailBuildRun = useBuildContext((v) => v.actions.onFailBuildRun);
  const onStopBuildRun = useBuildContext((v) => v.actions.onStopBuildRun);
  const onResetBuildRuns = useBuildContext((v) => v.actions.onResetBuildRuns);
  const onInvalidateBuildStudioActivityFeeds = useBuildContext(
    (v) => v.actions.onInvalidateBuildStudioActivityFeeds
  );
  const onInvalidateBuildForum = useBuildContext(
    (v) => v.actions.onInvalidateBuildForum
  );
  const onInvalidateBuildStudioBrowseTab = useBuildContext(
    (v) => v.actions.onInvalidateBuildStudioBrowseTab
  );
  const onPatchBuildSummary = useBuildContext(
    (v) => v.actions.onPatchBuildSummary
  );
  const onUpdateBuildCollaborationState = useChatContext(
    (v) => v.actions.onUpdateBuildCollaborationState
  );
  const onUpdateBuildContributionSubmissionState = useChatContext(
    (v) => v.actions.onUpdateBuildContributionSubmissionState
  );
  const onUpdateBuildContributionInviteNotification = useNotiContext(
    (v) => v.actions.onUpdateBuildContributionInviteNotification
  );
  const onPublishBuildRuntimeVerifyResult = useBuildContext(
    (v) => v.actions.onPublishBuildRuntimeVerifyResult
  );
  const buildRunsRef = useRef<Record<string, BuildLiveRunState>>(buildRuns);
  const buildRunRequestMapRef =
    useRef<Record<string, number>>(buildRunRequestMap);
  const buildWorkspacesRef =
    useRef<Record<string, BuildWorkspaceSnapshot>>(buildWorkspaces);
  const userIdRef = useRef(userId);
  const replayedResumeRunStateKeysRef = useRef<Record<string, string>>({});

  useEffect(() => {
    buildRunsRef.current = buildRuns;
    buildRunRequestMapRef.current = buildRunRequestMap;
    buildWorkspacesRef.current = buildWorkspaces;
  }, [buildRunRequestMap, buildRuns, buildWorkspaces]);

  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  useEffect(() => {
    onResetBuildRuns();
    replayedResumeRunStateKeysRef.current = {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    function resolveBuildId(
      requestId?: string,
      explicitBuildId?: number | null
    ) {
      const normalizedBuildId = Number(explicitBuildId || 0);
      if (normalizedBuildId > 0) return normalizedBuildId;
      const normalizedRequestId = String(requestId || '').trim();
      if (!normalizedRequestId) return 0;
      return (
        Number(buildRunRequestMapRef.current[normalizedRequestId] || 0) || 0
      );
    }

    function shouldHandleRun(
      requestId?: string,
      explicitBuildId?: number | null
    ) {
      const resolvedBuildId = resolveBuildId(requestId, explicitBuildId);
      if (!resolvedBuildId) {
        return {
          buildId: 0,
          shouldHandle: false
        };
      }
      return {
        buildId: resolvedBuildId,
        shouldHandle: true
      };
    }

    function findWorkspaceMessage(buildId: number, messageId?: number | null) {
      const normalizedMessageId = Number(messageId || 0);
      if (normalizedMessageId <= 0) return null;
      const workspace = buildWorkspacesRef.current[String(buildId)] || null;
      if (!Array.isArray(workspace?.chatMessages)) return null;
      return (
        workspace.chatMessages.find(
          (entry: any) => Number(entry?.id || 0) === normalizedMessageId
        ) || null
      );
    }

    function ensureBuildRunRegistered({
      requestId,
      buildId,
      runMode,
      status,
      userMessageContent,
      userMessageId,
      userClientMessageId,
      assistantMessageId,
      assistantClientMessageId,
      assistantMessageCreatedAt,
      lastActivityAt
    }: {
      requestId?: string;
      buildId: number;
      runMode?: 'user' | 'greeting' | 'runtime-autofix' | null;
      status?: string | null;
      userMessageContent?: string | null;
      userMessageId?: number | null;
      userClientMessageId?: string | null;
      assistantMessageId?: number | null;
      assistantClientMessageId?: string | null;
      assistantMessageCreatedAt?: number | null;
      lastActivityAt?: number | null;
    }) {
      const normalizedRequestId = String(requestId || '').trim();
      if (!normalizedRequestId || buildId <= 0) return;
      const workspace = buildWorkspacesRef.current[String(buildId)] || null;
      const userMessage =
        findWorkspaceMessage(buildId, userMessageId) ||
        (typeof userMessageContent === 'string'
          ? {
              id:
                Number(userMessageId || 0) > 0
                  ? Number(userMessageId)
                  : createFallbackBuildRunMessageId(),
              role: 'user' as const,
              content: userMessageContent,
              codeGenerated: null,
              billingState: null,
              streamCodePreview: null,
              artifactVersionId: null,
              clientMessageId: String(userClientMessageId || '').trim() || null,
              createdAt: Math.floor(Date.now() / 1000),
              persisted: Number(userMessageId || 0) > 0
            }
          : null);
      const assistantMessage =
        findWorkspaceMessage(buildId, assistantMessageId) ||
        (Number(assistantMessageId || 0) > 0 ||
        String(assistantClientMessageId || '').trim()
          ? {
              id:
                Number(assistantMessageId || 0) > 0
                  ? Number(assistantMessageId || 0)
                  : createFallbackBuildRunMessageId(),
              role: 'assistant' as const,
              content: '',
              codeGenerated: null,
              billingState: null,
              streamCodePreview: null,
              artifactVersionId: null,
              clientMessageId:
                String(assistantClientMessageId || '').trim() || null,
              createdAt:
                Number(assistantMessageCreatedAt || 0) > 0
                  ? Number(assistantMessageCreatedAt)
                  : Math.floor(Date.now() / 1000),
              persisted: Number(assistantMessageId || 0) > 0
            }
          : null);
      const existingRun = buildRunsRef.current[String(buildId)] || null;
      if (existingRun?.requestId === normalizedRequestId) {
        if (existingRun.userMessage || !userMessage) {
          return;
        }
        onUpdateBuildRunStream({
          buildId,
          requestId: normalizedRequestId,
          userMessageId:
            Number(userMessageId || 0) > 0 ? Number(userMessageId) : null,
          userMessageContent: userMessage.content,
          userClientMessageId:
            String(userClientMessageId || '').trim() || undefined,
          updatedAt:
            Number(lastActivityAt || 0) > 0
              ? Number(lastActivityAt)
              : Date.now()
        });
        return;
      }
      onRegisterBuildRun({
        buildId,
        requestId: normalizedRequestId,
        runMode:
          runMode === 'greeting' || runMode === 'runtime-autofix'
            ? runMode
            : 'user',
        generating: true,
        status: typeof status === 'string' ? status : null,
        assistantStatusSteps:
          typeof status === 'string' && status.trim().length > 0
            ? [status]
            : [],
        userMessage,
        assistantMessage,
        baseProjectFiles: Array.isArray(workspace?.build?.projectFiles)
          ? workspace.build.projectFiles
          : [],
        updatedAt:
          Number(lastActivityAt || 0) > 0 ? Number(lastActivityAt) : Date.now()
      });
    }

    function replayResumeRunState(payload: BuildResumeRunStatePayload) {
      const normalizedResumeRunState = normalizeBuildResumeRunState(payload);
      const resolvedRun = shouldHandleRun(
        normalizedResumeRunState.requestId,
        normalizedResumeRunState.buildId
      );
      if (!resolvedRun.shouldHandle) return;
      const replayKey = getBuildResumeRunStateReplayKey(
        normalizedResumeRunState
      );
      const replayLookupKey = [
        resolvedRun.buildId,
        String(normalizedResumeRunState.requestId || '').trim()
      ].join(':');
      if (
        replayedResumeRunStateKeysRef.current[replayLookupKey] === replayKey
      ) {
        return;
      }
      replayedResumeRunStateKeysRef.current[replayLookupKey] = replayKey;
      ensureBuildRunRegistered({
        requestId: normalizedResumeRunState.requestId,
        buildId: resolvedRun.buildId,
        runMode: normalizedResumeRunState.runMode,
        status: normalizedResumeRunState.status,
        userMessageContent:
          normalizedResumeRunState.streamUpdate?.userMessageContent,
        userMessageId: normalizedResumeRunState.streamUpdate?.userMessageId,
        userClientMessageId:
          normalizedResumeRunState.streamUpdate?.userClientMessageId,
        assistantMessageId:
          normalizedResumeRunState.streamUpdate?.assistantMessageId,
        assistantClientMessageId:
          normalizedResumeRunState.streamUpdate?.assistantClientMessageId,
        assistantMessageCreatedAt:
          normalizedResumeRunState.streamUpdate?.assistantMessageCreatedAt,
        lastActivityAt: normalizedResumeRunState.lastActivityAt
      });
      replayBuildResumeRunState({
        normalized: normalizedResumeRunState,
        onTerminalComplete: (terminalPayload) => {
          onCompleteBuildRun({
            buildId: resolvedRun.buildId,
            requestId: normalizedResumeRunState.requestId,
            assistantText: terminalPayload.assistantText,
            artifactCode:
              terminalPayload.artifact?.content ?? terminalPayload.code ?? null,
            projectFiles:
              Array.isArray(terminalPayload.projectFiles) &&
              terminalPayload.projectFiles.length > 0
                ? terminalPayload.projectFiles
                : null,
            ...(Array.isArray(terminalPayload.projectFiles) &&
            terminalPayload.projectFiles.length > 0 &&
            typeof terminalPayload.projectFilesHash === 'string' &&
            terminalPayload.projectFilesHash.trim()
              ? {
                  projectFilesHash: terminalPayload.projectFilesHash.trim()
                }
              : {}),
            ...(Object.prototype.hasOwnProperty.call(
              terminalPayload || {},
              'interruptionReason'
            )
              ? {
                  interruptionReason: terminalPayload.interruptionReason ?? null
                }
              : {}),
            ...(Object.prototype.hasOwnProperty.call(
              terminalPayload || {},
              'workspaceChanged'
            )
              ? {
                  workspaceChanged: terminalPayload.workspaceChanged === true
                }
              : {}),
            executionPlan: terminalPayload.executionPlan,
            followUpPrompt: Object.prototype.hasOwnProperty.call(
              terminalPayload || {},
              'followUpPrompt'
            )
              ? (terminalPayload.followUpPrompt ?? null)
              : undefined,
            ...(Object.prototype.hasOwnProperty.call(
              terminalPayload || {},
              'pendingToolApproval'
            )
              ? {
                  pendingToolApproval:
                    terminalPayload.pendingToolApproval ?? null
                }
              : {}),
            ...(Object.prototype.hasOwnProperty.call(
              terminalPayload || {},
              'thumbnailNudge'
            )
              ? { thumbnailNudge: terminalPayload.thumbnailNudge ?? null }
              : {}),
            deferredBuildRequest: Object.prototype.hasOwnProperty.call(
              terminalPayload || {},
              'deferredBuildRequest'
            )
              ? (terminalPayload.deferredBuildRequest ?? null)
              : undefined,
            ...(Object.prototype.hasOwnProperty.call(
              terminalPayload || {},
              'runtimeExplorationPlan'
            )
              ? {
                  runtimeExplorationPlan:
                    terminalPayload.runtimeExplorationPlan ?? null
                }
              : {}),
            ...(Object.prototype.hasOwnProperty.call(
              terminalPayload || {},
              'runtimePlanRefined'
            )
              ? {
                  runtimePlanRefined: Boolean(
                    terminalPayload.runtimePlanRefined
                  )
                }
              : {}),
            billingState: terminalPayload.billingState ?? null,
            requestLimits: getBuildRequestLimitsFromPayload(terminalPayload),
            lifecycle: terminalPayload.lifecycle ?? null,
            artifactVersionId:
              Number(terminalPayload?.message?.artifactVersionId || 0) > 0
                ? Number(terminalPayload.message.artifactVersionId)
                : Number(terminalPayload?.artifact?.versionId || 0) > 0
                  ? Number(terminalPayload.artifact.versionId)
                  : null,
            persistedAssistantId:
              Number(terminalPayload?.message?.id || 0) > 0
                ? Number(terminalPayload.message.id)
                : null,
            persistedUserId:
              Number(terminalPayload?.message?.userMessageId || 0) > 0
                ? Number(terminalPayload.message.userMessageId)
                : null,
            userClientMessageId:
              typeof terminalPayload?.message?.userClientMessageId === 'string'
                ? terminalPayload.message.userClientMessageId
                : undefined,
            assistantClientMessageId:
              typeof terminalPayload?.message?.assistantClientMessageId ===
              'string'
                ? terminalPayload.message.assistantClientMessageId
                : undefined,
            createdAt:
              Number(terminalPayload?.message?.createdAt || 0) > 0
                ? Number(terminalPayload.message.createdAt)
                : undefined
          });
        },
        onTerminalError: (terminalPayload) => {
          onFailBuildRun({
            buildId: resolvedRun.buildId,
            requestId: normalizedResumeRunState.requestId,
            error:
              terminalPayload.error ||
              'Lumine had trouble with that request. Please try again.',
            requestLimits: getBuildRequestLimitsFromPayload(terminalPayload),
            lifecycle: terminalPayload.lifecycle ?? null
          });
        },
        onTerminalStopped: (terminalPayload) => {
          onStopBuildRun({
            buildId: resolvedRun.buildId,
            requestId: normalizedResumeRunState.requestId,
            stopReason: terminalPayload.stopReason || null,
            lifecycle: terminalPayload.lifecycle ?? null,
            ...(typeof terminalPayload.assistantText === 'string'
              ? { assistantText: terminalPayload.assistantText }
              : {})
          });
        },
        onRunningSnapshot: (runningSnapshot) => {
          onApplyBuildRunRunningSnapshot({
            buildId: resolvedRun.buildId,
            requestId: normalizedResumeRunState.requestId,
            runningSnapshot: {
              status: runningSnapshot.status,
              assistantStatusSteps: runningSnapshot.assistantStatusSteps,
              agentContext: runningSnapshot.agentContext,
              lifecycle: runningSnapshot.lifecycle,
              usageMetrics: runningSnapshot.usageMetrics,
              updatedAt: runningSnapshot.lastActivityAt
            }
          });
        },
        onRunEvent: (runEvent) => {
          onAppendBuildRunEvent({
            buildId: resolvedRun.buildId,
            requestId: normalizedResumeRunState.requestId,
            event: runEvent,
            updatedAt: normalizedResumeRunState.lastActivityAt
          });
        },
        onStreamUpdate: (streamUpdate) => {
          onUpdateBuildRunStream({
            buildId: resolvedRun.buildId,
            requestId: normalizedResumeRunState.requestId,
            ...streamUpdate,
            updatedAt: normalizedResumeRunState.lastActivityAt
          });
        }
      });
    }

    function handleGenerateUpdate({
      requestId,
      buildId,
      runMode,
      status,
      assistantStatusSteps,
      reply,
      replyDelta,
      codeGenerated,
      codeGeneratedDelta,
      userMessageId,
      userClientMessageId,
      userMessageContent,
      assistantMessageId,
      assistantClientMessageId,
      assistantMessageCreatedAt,
      usageMetrics,
      baseProjectFiles,
      projectFiles,
      projectFilesMode,
      projectFilesPersisted,
      projectFilesFocusPath
    }: {
      requestId?: string;
      buildId?: number | null;
      runMode?: 'user' | 'greeting' | 'runtime-autofix' | null;
      status?: string | null;
      assistantStatusSteps?: string[];
      reply?: string;
      replyDelta?: string;
      codeGenerated?: string | null;
      codeGeneratedDelta?: string;
      userMessageId?: number | null;
      userClientMessageId?: string | null;
      userMessageContent?: string | null;
      assistantMessageId?: number | null;
      assistantClientMessageId?: string | null;
      assistantMessageCreatedAt?: number | null;
      usageMetrics?: Record<string, BuildLiveRunUsageMetric> | null;
      baseProjectFiles?: Array<{ path: string; content?: string }> | null;
      projectFiles?: Array<{ path: string; content?: string }> | null;
      projectFilesMode?: 'patch' | 'snapshot' | null;
      projectFilesPersisted?: boolean;
      projectFilesFocusPath?: string | null;
    }) {
      const resolvedRun = shouldHandleRun(requestId, buildId);
      if (!resolvedRun.shouldHandle || !requestId) return;
      const payload = arguments[0] || {};
      const latestAssistantStatus =
        typeof status === 'string'
          ? status
          : Array.isArray(assistantStatusSteps)
            ? String(
                assistantStatusSteps[assistantStatusSteps.length - 1] || ''
              ).trim() || null
            : null;
      ensureBuildRunRegistered({
        requestId,
        buildId: resolvedRun.buildId,
        runMode,
        status: latestAssistantStatus,
        userMessageContent,
        userMessageId,
        userClientMessageId,
        assistantMessageId,
        assistantClientMessageId,
        assistantMessageCreatedAt
      });
      const buildRun: any = {
        buildId: resolvedRun.buildId,
        requestId
      };
      if (Object.prototype.hasOwnProperty.call(payload, 'status')) {
        buildRun.status = typeof status === 'string' ? status : null;
      }
      if (
        Object.prototype.hasOwnProperty.call(payload, 'assistantStatusSteps')
      ) {
        buildRun.assistantStatusSteps = Array.isArray(assistantStatusSteps)
          ? assistantStatusSteps.filter(
              (step): step is string =>
                typeof step === 'string' && step.trim().length > 0
            )
          : [];
      }
      if (typeof reply === 'string') {
        buildRun.reply = reply;
      }
      if (typeof replyDelta === 'string') {
        buildRun.replyDelta = replyDelta;
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'codeGenerated')) {
        buildRun.codeGenerated = codeGenerated ?? null;
      }
      if (typeof codeGeneratedDelta === 'string') {
        buildRun.codeGeneratedDelta = codeGeneratedDelta;
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'userMessageId')) {
        buildRun.userMessageId =
          Number(userMessageId || 0) > 0 ? Number(userMessageId) : null;
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'userMessageContent')) {
        buildRun.userMessageContent =
          typeof userMessageContent === 'string' ? userMessageContent : null;
      }
      if (
        Object.prototype.hasOwnProperty.call(payload, 'userClientMessageId')
      ) {
        buildRun.userClientMessageId =
          typeof userClientMessageId === 'string'
            ? userClientMessageId.trim() || null
            : null;
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'assistantMessageId')) {
        buildRun.assistantMessageId =
          Number(assistantMessageId || 0) > 0
            ? Number(assistantMessageId)
            : null;
      }
      if (
        Object.prototype.hasOwnProperty.call(
          payload,
          'assistantClientMessageId'
        )
      ) {
        buildRun.assistantClientMessageId =
          typeof assistantClientMessageId === 'string'
            ? assistantClientMessageId.trim() || null
            : null;
      }
      if (
        Object.prototype.hasOwnProperty.call(
          payload,
          'assistantMessageCreatedAt'
        )
      ) {
        buildRun.assistantMessageCreatedAt =
          Number(assistantMessageCreatedAt || 0) > 0
            ? Number(assistantMessageCreatedAt)
            : null;
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'usageMetrics')) {
        buildRun.usageMetrics =
          usageMetrics && typeof usageMetrics === 'object' ? usageMetrics : {};
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'baseProjectFiles')) {
        buildRun.baseProjectFiles = Array.isArray(baseProjectFiles)
          ? baseProjectFiles
          : [];
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'projectFiles')) {
        buildRun.projectFiles = Array.isArray(projectFiles) ? projectFiles : [];
        buildRun.projectFilesMode =
          projectFilesMode === 'snapshot' ? 'snapshot' : 'patch';
        buildRun.projectFilesPersisted = projectFilesPersisted === true;
        buildRun.projectFilesFocusPath =
          String(projectFilesFocusPath || '').trim() || null;
      }
      onUpdateBuildRunStream(buildRun);
    }

    function handleGenerateComplete({
      requestId,
      buildId,
      runMode,
      assistantText,
      artifact,
      code,
      projectFiles,
      projectFilesHash,
      interruptionReason,
      executionPlan,
      followUpPrompt,
      pendingToolApproval,
      thumbnailNudge,
      runtimeExplorationPlan,
      runtimePlanRefined,
      workspaceChanged,
      billingState,
      requestLimits,
      billing,
      deferredBuildRequest,
      lifecycle,
      message
    }: {
      requestId?: string;
      buildId?: number | null;
      runMode?: 'user' | 'greeting' | 'runtime-autofix' | null;
      assistantText?: string;
      artifact?: {
        content?: string;
        versionId?: number | null;
      };
      code?: string | null;
      projectFiles?: Array<{ path: string; content?: string }> | null;
      projectFilesHash?: string | null;
      interruptionReason?:
        | 'tool_limit'
        | 'energy_depleted'
        | 'energy_budget'
        | 'awaiting_approval'
        | null;
      executionPlan?: any | null;
      followUpPrompt?: {
        question?: string | null;
        suggestedMessage?: string | null;
        sourceMessageId?: number | null;
        modelSwitch?: { model: string; mode: string; label: string } | null;
        mode?: string | null;
      } | null;
      pendingToolApproval?: Record<string, any> | null;
      thumbnailNudge?: { sourceMessageId: number } | null;
      runtimeExplorationPlan?: any | null;
      runtimePlanRefined?: boolean;
      workspaceChanged?: boolean;
      billingState?: 'charged' | 'not_charged' | 'pending' | null;
      requestLimits?: any | null;
      billing?: { snapshot?: any | null } | null;
      deferredBuildRequest?: {
        message?: string | null;
        messageContext?: string | null;
        planAction?: 'continue' | 'cancel' | 'pivot' | null;
        stopActiveRun?: boolean | null;
        stopRequestId?: string | null;
      } | null;
      lifecycle?: Record<string, any> | null;
      message?: {
        id?: number | null;
        userMessageId?: number | null;
        userMessageContent?: string | null;
        userClientMessageId?: string | null;
        assistantClientMessageId?: string | null;
        artifactVersionId?: number | null;
        createdAt?: number;
      };
    }) {
      const resolvedRun = shouldHandleRun(requestId, buildId);
      if (!resolvedRun.shouldHandle || !requestId) return;
      ensureBuildRunRegistered({
        requestId,
        buildId: resolvedRun.buildId,
        runMode,
        userMessageContent:
          typeof message?.userMessageContent === 'string'
            ? message.userMessageContent
            : null,
        userMessageId: message?.userMessageId,
        userClientMessageId: message?.userClientMessageId,
        assistantMessageId: message?.id,
        assistantClientMessageId: message?.assistantClientMessageId,
        assistantMessageCreatedAt: message?.createdAt
      });
      onCompleteBuildRun({
        buildId: resolvedRun.buildId,
        requestId,
        assistantText,
        artifactCode: artifact?.content ?? code ?? null,
        projectFiles:
          Array.isArray(projectFiles) && projectFiles.length > 0
            ? projectFiles
            : null,
        ...(Array.isArray(projectFiles) &&
        projectFiles.length > 0 &&
        typeof projectFilesHash === 'string' &&
        projectFilesHash.trim()
          ? {
              projectFilesHash: projectFilesHash.trim()
            }
          : {}),
        interruptionReason,
        executionPlan,
        followUpPrompt: Object.prototype.hasOwnProperty.call(
          arguments[0] || {},
          'followUpPrompt'
        )
          ? (followUpPrompt ?? null)
          : undefined,
        ...(Object.prototype.hasOwnProperty.call(
          arguments[0] || {},
          'pendingToolApproval'
        )
          ? { pendingToolApproval: pendingToolApproval ?? null }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(
          arguments[0] || {},
          'thumbnailNudge'
        )
          ? { thumbnailNudge: thumbnailNudge ?? null }
          : {}),
        deferredBuildRequest: Object.prototype.hasOwnProperty.call(
          arguments[0] || {},
          'deferredBuildRequest'
        )
          ? (deferredBuildRequest ?? null)
          : undefined,
        ...(Object.prototype.hasOwnProperty.call(
          arguments[0] || {},
          'runtimeExplorationPlan'
        )
          ? {
              runtimeExplorationPlan: runtimeExplorationPlan ?? null
            }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(
          arguments[0] || {},
          'runtimePlanRefined'
        )
          ? {
              runtimePlanRefined: Boolean(runtimePlanRefined)
            }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(
          arguments[0] || {},
          'workspaceChanged'
        )
          ? { workspaceChanged: workspaceChanged === true }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(
          arguments[0] || {},
          'lifecycle'
        )
          ? { lifecycle: lifecycle ?? null }
          : {}),
        billingState: billingState ?? null,
        requestLimits: requestLimits || billing?.snapshot || null,
        artifactVersionId:
          Number(message?.artifactVersionId || 0) > 0
            ? Number(message?.artifactVersionId)
            : Number(artifact?.versionId || 0) > 0
              ? Number(artifact?.versionId)
              : null,
        persistedAssistantId:
          Number(message?.id || 0) > 0 ? Number(message?.id) : null,
        persistedUserId:
          Number(message?.userMessageId || 0) > 0
            ? Number(message?.userMessageId)
            : null,
        userClientMessageId:
          typeof message?.userClientMessageId === 'string'
            ? message.userClientMessageId
            : undefined,
        assistantClientMessageId:
          typeof message?.assistantClientMessageId === 'string'
            ? message.assistantClientMessageId
            : undefined,
        createdAt:
          Number(message?.createdAt || 0) > 0 ? message?.createdAt : undefined
      });
    }

    function handleGenerateError({
      requestId,
      buildId,
      runMode,
      error,
      requestLimits,
      lifecycle,
      code,
      activeRequestId,
      activeBuildId
    }: {
      requestId?: string;
      buildId?: number | null;
      runMode?: 'user' | 'greeting' | 'runtime-autofix' | null;
      error?: string;
      requestLimits?: any | null;
      lifecycle?: Record<string, any> | null;
      code?: string;
      activeRequestId?: string | null;
      activeBuildId?: number | null;
    }) {
      const resolvedRun = shouldHandleRun(requestId, buildId);
      if (!resolvedRun.shouldHandle || !requestId) return;
      ensureBuildRunRegistered({
        requestId,
        buildId: resolvedRun.buildId,
        runMode
      });
      onFailBuildRun({
        buildId: resolvedRun.buildId,
        requestId,
        error:
          error || 'Lumine had trouble with that request. Please try again.',
        requestLimits: requestLimits || null,
        ...(Object.prototype.hasOwnProperty.call(
          arguments[0] || {},
          'lifecycle'
        )
          ? { lifecycle: lifecycle ?? null }
          : {})
      });
      if (
        code === 'lumine_busy_other_project' &&
        activeRequestId &&
        Number(activeBuildId) > 0
      ) {
        socket.emit('build_resume_run', {
          buildId: Number(activeBuildId),
          requestId: activeRequestId,
          resumeReason: 'busy_refusal'
        });
      }
    }

    function handleGenerateStopped({
      requestId,
      buildId,
      runMode,
      deduped,
      guardStatus,
      assistantText,
      stopReason,
      lifecycle
    }: {
      requestId?: string;
      buildId?: number | null;
      runMode?: 'user' | 'greeting' | 'runtime-autofix' | null;
      deduped?: boolean;
      guardStatus?: 'processing' | 'completed' | 'conflict';
      assistantText?: string;
      stopReason?: 'user' | 'replacement' | null;
      lifecycle?: Record<string, any> | null;
    }) {
      const resolvedRun = shouldHandleRun(requestId, buildId);
      if (!resolvedRun.shouldHandle || !requestId) return;
      ensureBuildRunRegistered({
        requestId,
        buildId: resolvedRun.buildId,
        runMode
      });
      if (deduped && guardStatus === 'processing') {
        const existingRun =
          buildRunsRef.current[String(resolvedRun.buildId)] || null;
        onUpdateBuildRunStatus({
          buildId: resolvedRun.buildId,
          requestId,
          status:
            String(existingRun?.status || '').trim() === 'Stopping...'
              ? 'Stopping...'
              : 'Recovering live response...'
        });
        return;
      }
      onStopBuildRun({
        buildId: resolvedRun.buildId,
        requestId,
        stopReason: stopReason || null,
        ...(Object.prototype.hasOwnProperty.call(
          arguments[0] || {},
          'lifecycle'
        )
          ? { lifecycle: lifecycle ?? null }
          : {}),
        ...(typeof assistantText === 'string' ? { assistantText } : {})
      });
    }

    function handleRunEvent({
      requestId,
      buildId,
      runMode,
      event
    }: {
      requestId?: string;
      buildId?: number | null;
      runMode?: 'user' | 'greeting' | 'runtime-autofix' | null;
      event?: {
        buildId?: number | null;
      } & BuildRunEventEnvelopePayload;
    }) {
      const normalizedRequestId =
        String(requestId || event?.requestId || '').trim() || undefined;
      const resolvedRun = shouldHandleRun(
        normalizedRequestId,
        Number(event?.buildId || 0) > 0 ? Number(event?.buildId) : buildId
      );
      const normalizedEvent = normalizeBuildRunEventEnvelope({
        event,
        requestId: normalizedRequestId,
        buildId: resolvedRun.buildId,
        userId
      });
      if (
        !resolvedRun.shouldHandle ||
        !normalizedRequestId ||
        !normalizedEvent
      ) {
        return;
      }
      ensureBuildRunRegistered({
        requestId: normalizedRequestId,
        buildId: resolvedRun.buildId,
        runMode
      });
      onAppendBuildRunEvent({
        buildId: resolvedRun.buildId,
        requestId: normalizedRequestId,
        event: normalizedEvent
      });
    }

    function handleRuntimeVerifyComplete({
      buildId,
      requestId,
      improved,
      reason,
      shouldRepairAgain,
      nextRemainingRepairs
    }: {
      buildId?: number | null;
      requestId?: string;
      improved?: boolean;
      reason?: string;
      shouldRepairAgain?: boolean;
      nextRemainingRepairs?: number;
    }) {
      const normalizedRequestId = String(requestId || '').trim();
      if (!normalizedRequestId) return;
      onPublishBuildRuntimeVerifyResult({
        buildId: Number(buildId || 0) > 0 ? Number(buildId) : null,
        requestId: normalizedRequestId,
        status: 'complete',
        improved: improved === true,
        reason: typeof reason === 'string' ? reason : null,
        shouldRepairAgain: shouldRepairAgain === true,
        nextRemainingRepairs
      });
    }

    function handleRuntimeVerifyError({
      buildId,
      requestId,
      error
    }: {
      buildId?: number | null;
      requestId?: string;
      error?: string;
    }) {
      const normalizedRequestId = String(requestId || '').trim();
      if (!normalizedRequestId) return;
      onPublishBuildRuntimeVerifyResult({
        buildId: Number(buildId || 0) > 0 ? Number(buildId) : null,
        requestId: normalizedRequestId,
        status: 'error',
        error:
          typeof error === 'string' && error.trim().length > 0
            ? error
            : 'Runtime verification failed.'
      });
    }

    function resumeTrackedBuildRuns() {
      for (const buildRun of Object.values(buildRunsRef.current)) {
        if (!buildRun?.generating || !buildRun.requestId) continue;
        socket.emit('build_resume_run', {
          buildId: Number(buildRun.buildId || 0) || undefined,
          requestId: buildRun.requestId
        });
      }
    }

    function handleVisibilityChange() {
      if (document.visibilityState !== 'visible') return;
      resumeTrackedBuildRuns();
    }

    function handleBuildActivityUpdated({
      buildId,
      forumUpdated
    }: {
      buildId?: number | null;
      forumUpdated?: boolean;
    }) {
      onInvalidateBuildStudioActivityFeeds({ userId: userIdRef.current });
      if (forumUpdated && Number(buildId || 0) > 0) {
        onInvalidateBuildForum({ rootBuildId: Number(buildId) });
      }
    }

    function handleBuildCollaborationUpdated({
      buildId,
      invite,
      inviteId,
      inviteStatus,
      request,
      requestId,
      requestStatus,
      eventTimeMs,
      timeStamp
    }: {
      buildId?: number;
      invite?: Record<string, any> | null;
      inviteId?: number;
      inviteStatus?: 'pending' | 'accepted' | 'declined' | 'revoked' | 'left';
      request?: Record<string, any> | null;
      requestId?: number;
      requestStatus?:
        'pending' | 'invited' | 'accepted' | 'rejected' | 'canceled';
      eventTimeMs?: number;
      timeStamp?: number;
    }) {
      onUpdateBuildCollaborationState({
        invite,
        inviteId,
        inviteStatus,
        request,
        requestId,
        requestStatus,
        eventTimeMs,
        timeStamp
      });
      onInvalidateBuildStudioBrowseTab({
        tab: 'collaborating',
        userId: userIdRef.current
      });
      if (invite || inviteId) {
        onUpdateBuildContributionInviteNotification({
          invite,
          inviteId: Number(invite?.id || inviteId || 0),
          status: inviteStatus,
          eventTimeMs
        });
      }
      patchViewerCollaborationBuildSummary({
        buildId,
        eventTimeMs,
        invite,
        inviteStatus,
        request,
        timeStamp
      });
    }

    function patchViewerCollaborationBuildSummary({
      buildId,
      eventTimeMs,
      invite,
      inviteStatus,
      request,
      timeStamp
    }: {
      buildId?: number;
      eventTimeMs?: number;
      invite?: Record<string, any> | null;
      inviteStatus?: string;
      request?: Record<string, any> | null;
      timeStamp?: number;
    }) {
      const update = getViewerCollaborationBuildSummaryPatch({
        viewerId: Number(userIdRef.current || 0),
        buildId,
        eventTimeMs,
        invite,
        inviteStatus,
        request,
        timeStamp
      });
      if (update) onPatchBuildSummary(update);
    }

    function handleBuildContributionLumineFixUpdated(payload: unknown) {
      const update = resolveBuildContributionLumineFixSocketUpdate(payload);
      if (!update) return;
      onUpdateBuildContributionSubmissionState(update);
    }

    socket.on('build_generate_update', handleGenerateUpdate);
    socket.on('build_generate_complete', handleGenerateComplete);
    socket.on('build_generate_error', handleGenerateError);
    socket.on('build_generate_stopped', handleGenerateStopped);
    socket.on('build_run_event', handleRunEvent);
    socket.on('build_resume_run_state', replayResumeRunState);
    socket.on('build_runtime_verify_complete', handleRuntimeVerifyComplete);
    socket.on('build_runtime_verify_error', handleRuntimeVerifyError);
    socket.on('build_activity_updated', handleBuildActivityUpdated);
    socket.on('build_collaboration_updated', handleBuildCollaborationUpdated);
    socket.on(
      'build_contribution_lumine_fix_updated',
      handleBuildContributionLumineFixUpdated
    );
    socket.on('connect', resumeTrackedBuildRuns);
    window.addEventListener('pageshow', resumeTrackedBuildRuns);
    window.addEventListener('online', resumeTrackedBuildRuns);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      socket.off('build_generate_update', handleGenerateUpdate);
      socket.off('build_generate_complete', handleGenerateComplete);
      socket.off('build_generate_error', handleGenerateError);
      socket.off('build_generate_stopped', handleGenerateStopped);
      socket.off('build_run_event', handleRunEvent);
      socket.off('build_resume_run_state', replayResumeRunState);
      socket.off('build_runtime_verify_complete', handleRuntimeVerifyComplete);
      socket.off('build_runtime_verify_error', handleRuntimeVerifyError);
      socket.off('build_activity_updated', handleBuildActivityUpdated);
      socket.off(
        'build_collaboration_updated',
        handleBuildCollaborationUpdated
      );
      socket.off(
        'build_contribution_lumine_fix_updated',
        handleBuildContributionLumineFixUpdated
      );
      socket.off('connect', resumeTrackedBuildRuns);
      window.removeEventListener('pageshow', resumeTrackedBuildRuns);
      window.removeEventListener('online', resumeTrackedBuildRuns);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
